import type { TrackingSpecs } from '@/libraries/tracking';

import { ObjectType } from '@/@types/graphql-types';
import { createSpecTracker, defineSpec } from '@/libraries/tracking';

const specs = {
	// AUTHOR
	clickOnAuthor: defineSpec<{
		property: string;
		useLastRoute: boolean;
	}>()({
		category: 'persons_credit_name',
		action: 'clicked',
		label: 'AUTHOR',
	}),

	clickContentFilter: defineSpec<{
		property: 'articles' | 'lists' | 'other';
	}>()({
		category: 'userinteraction',
		action: 'filter_toggled',
		label: 'content_types',
	}),

	clickOnAuthorLinks: defineSpec<{
		category: 'person_social_media' | 'person_publication';
		property: string;
	}>()({
		action: 'clicked',
		label: 'AUTHOR',
	}),

	// NEWS & LIST
	clickArticleLink: defineSpec<{
		label: 'internal' | 'external';
		property: string;
	}>()({
		category: 'userinteraction',
		action: 'content_clicked',
	}),

	// NEWS ARTICLE
	clickPlayOnTrailer: defineSpec<{
		action: ObjectType;
	}>()({
		category: 'youtube_playing',
		label: 'backdrop',
	}),

	// LIST ARTICLE
	clicksOnTitle: {
		category: 'userinteraction',
		action: 'title_clicked',
		label: 'editorial-lists',
	},

	clickOnReadMore: defineSpec<{
		action: 'expanded' | 'collapsed';
	}>()({
		category: 'content_header',
	}),

	togglePopover: defineSpec<{
		action: 'opened' | 'closed';
	}>()({
		category: 'userinteraction',
		label: 'more_actions',
	}),

	googleSourceImpression: {
		category: 'google_preferred_source',
		action: 'impression',
	},

	clickGoogleSource: {
		category: 'google_preferred_source',
		action: 'click',
		// (this one should have cc_interaction isActive = true)
	},

	// GUIDE PAGE
	clickGuideItemLink: defineSpec<{
		property: 'editorial-list' | 'news' | 'title' | 'review';
		label: string;
		value: number;
	}>()({
		category: 'userinteraction',
		action: 'content_clicked',
	}),

	// YOUTUBE SHORTS MODULE
	shortsImpression: {
		category: 'module',
		action: 'impression',
		label: 'justwatch_shorts',
		nonInteraction: true,
	},

	shortsPreviewPlaying: defineSpec<{
		property: string; // video id
	}>()({
		category: 'youtube_playing',
		action: 'short',
		label: 'justwatch_shorts',
		nonInteraction: true,
	}),

	shortsStarted: defineSpec<{
		property: string;
	}>()({
		category: 'youtube_started',
		action: 'short',
		label: 'justwatch_shorts',
	}),

	shortsResumed: defineSpec<{
		property: string;
	}>()({
		category: 'youtube_playing',
		action: 'short',
		label: 'justwatch_shorts',
	}),

	shortsPaused: defineSpec<{
		property: string;
	}>()({
		category: 'youtube_paused',
		action: 'short',
		label: 'justwatch_shorts',
	}),

	shortsCompleted: defineSpec<{
		property: string;
	}>()({
		category: 'youtube_ended',
		action: 'short',
		label: 'justwatch_shorts',
		nonInteraction: true,
	}),

	shortsYoutubeClicked: defineSpec<{
		property: string; // video id
	}>()({
		category: 'userinteraction',
		action: 'youtube_clicked',
		label: 'justwatch_shorts',
	}),

	shortsMuteToggled: defineSpec<{
		property: string; // video id
		value: 0 | 1;
	}>()({
		category: 'userinteraction',
		action: 'mute_button_toggled',
		label: 'justwatch_shorts',
	}),

	shortsNavigation: defineSpec<{
		label: 'left' | 'right';
	}>()({
		category: 'userinteraction',
		action: 'navigation_clicked',
		property: 'justwatch_shorts',
	}),

	shortsModalOpened: {
		category: 'userinteraction',
		action: 'modal_opened',
		label: 'JustWatchShortsModal',
	},

	shortsModalClosed: {
		category: 'userinteraction',
		action: 'modal_closed',
		label: 'JustWatchShortsModal',
	},

	shortsProgress: defineSpec<{
		property: string; // video id
		value: 0 | 25 | 50 | 75 | 100;
	}>()({
		category: 'userinteraction',
		action: 'video_view',
		label: 'justwatch_shorts',
		nonInteraction: true,
	}),
} satisfies TrackingSpecs;

export function useEditorialTracking() {
	const trackEditorial = createSpecTracker({ specs });

	return {
		trackEditorial,
	};
}
